import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Snackbar,
  Alert
} from '@mui/material';
import { Link } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonalInjuryIcon from '@mui/icons-material/PersonalInjury';
import unifiedApi from '../../utils/unifiedApi';
import { 
  useCanManageTeam, 
  useCanManageLeague, 
  useIsAdmin, 
  useHasAnyRole 
} from '../../contexts/PermissionsContext';

const GameAvailabilityTable = ({ teamId, leagueId }) => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [gameDetails, setGameDetails] = useState([]);
  const [currentUserTenantId, setCurrentUserTenantId] = useState(null);
  const [updateError, setUpdateError] = useState(null);
  
  // Permission hooks
  const canManageTeam = useCanManageTeam(parseInt(teamId));
  const canManageLeague = useCanManageLeague(parseInt(leagueId));
  const isAdmin = useIsAdmin();
  const canManageAvailability = useHasAnyRole(['super-admin', 'tenant-level-admin', 'league-manager', 'spares-coordinator']);

  useEffect(() => {
    if (!teamId) return;
    setLoading(true);
    
    // Fetch current user profile to get UserTenantId
    const fetchData = async () => {
      try {
        // Get current user's userTenantId
        const userData = await unifiedApi.get('/api/me');
        if (userData) {
          setCurrentUserTenantId(userData.userTenantId);
        }

        // Fetch availability
        const data = await unifiedApi.get(`/api/teams/${teamId}/upcoming-games/availability`);
        if (data) {
          setGames(data);
          
          // Fetch game details for all upcoming games
          const gameIds = data.map(g => g.gameId);
          const details = await unifiedApi.get(`/api/games/team/${teamId}`);
          if (details) {
            // Only keep details for games in our table
            setGameDetails(details.filter(d => gameIds.includes(d.id)));
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setGames([]);
        setGameDetails([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [teamId]);

  // Build unique player list — non-injured sorted alphabetically, then injured sorted alphabetically at the bottom
  const allPlayers = games.length
    ? games[0].players
        .map(p => ({
          userId: p.userId,
          userTenantId: p.userTenantId,
          name: `${p.firstName} ${p.lastName}`,
          isInjured: p.playerStatus === 'Injured',
        }))
        .sort((a, b) => {
          if (a.isInjured !== b.isInjured) return a.isInjured ? 1 : -1;
          return a.name.localeCompare(b.name);
        })
    : [];

  // Helper to get game info
  const getGameInfo = (gameId) => {
    const game = gameDetails.find(g => g.id === gameId);
    if (!game) return null;
    let isHome = game.homeTeamId === teamId || String(game.homeTeamId) === String(teamId);
    const isByeGame = game.gameType === 5 || !game.awayTeamId;
    let otherTeam = isByeGame ? 'BYE' : (isHome ? game.awayTeamName : game.homeTeamName);
    let homeAway = isByeGame ? '' : (isHome ? 'Home at' : 'Away at');
    // Format date/time
    const dateObj = new Date(game.start);
    const dateStr = dateObj.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    const timeStr = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    // Format venue info
    const venueInfo = game.fieldName && game.venueName 
      ? `${game.fieldName} - ${game.venueName}`
      : game.venueName || game.fieldName || '';
    return { homeAway, otherTeam, dateStr, timeStr, venueInfo };
  };

  const refreshAvailability = async () => {
    setRefreshing(true);
    try {
      // Get current user's userTenantId (if not already set)
      if (!currentUserTenantId) {
        const userData = await unifiedApi.get('/api/me');
        if (userData) {
          setCurrentUserTenantId(userData.userTenantId);
        }
      }

      // Add cache-busting timestamp to force fresh data
      const timestamp = Date.now();
      
      // Fetch availability with cache-busting
      const data = await unifiedApi.get(`/api/teams/${teamId}/upcoming-games/availability?_t=${timestamp}`);
      if (data) {
        setGames(data);
        
        // Fetch game details
        const gameIds = data.map(g => g.gameId);
        const details = await unifiedApi.get(`/api/games/team/${teamId}?_t=${timestamp}`);
        if (details) {
          setGameDetails(details.filter(d => gameIds.includes(d.id)));
        }
      }
    } catch (error) {
      console.error('Error refreshing availability:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Function to check if current user can update a specific player's availability
  const canUpdatePlayerAvailability = (playerUserTenantId) => {
    // Super admins, tenant admins, league managers, and spares coordinators can edit anyone's availability within their scope
    if (isAdmin || canManageTeam || canManageLeague || canManageAvailability) {
      return true;
    }
    
    // Players can only update their own availability
    return currentUserTenantId === playerUserTenantId;
  };

  // Update RSVP
  const updateAvailability = async (eventId, userTenantId, attending, rsvpId) => {
    // Check permissions before allowing the update
    if (!canUpdatePlayerAvailability(userTenantId)) {
      console.warn('User does not have permission to update this player\'s availability');
      return;
    }

    try {
      let saved;
      if (rsvpId !== undefined && rsvpId !== null && rsvpId !== '') {
        // Update existing RSVP
        saved = await unifiedApi.put(`/api/rsvps/${rsvpId}`, { attending });
      } else {
        // Create new RSVP
        saved = await unifiedApi.post('/api/rsvps', { eventId, userTenantId, attending });
      }
      // unifiedApi returns null on 404 (e.g. the game or RSVP was deleted)
      if (!saved) {
        setUpdateError('Couldn\'t update availability. Please try again.');
      }
      await refreshAvailability();
    } catch (error) {
      console.error('Error updating availability:', error);
      setUpdateError('Couldn\'t update availability. Please try again.');
    }
  };
  return (
    <Box mt={2}>
      <Typography variant="h6" gutterBottom>
        Upcoming Games Availability
      </Typography>
      {loading ? (
        <Typography>Loading...</Typography>
      ) : games.length === 0 ? (
        <Typography>No upcoming games or availability data.</Typography>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell> <span style={{ fontWeight: 'bold' }}>Player</span></TableCell>
                {games.map(game => {
                  const info = getGameInfo(game.gameId);
                  
                  // Calculate counts for this game (exclude injured players)
                  const activePlayers = game.players.filter(p => p.playerStatus !== 'Injured');
                  const going = activePlayers.filter(p => p.status === true).length;
                  const notGoing = activePlayers.filter(p => p.status === false).length;
                  const noResponse = activePlayers.filter(p => p.status === null || p.status === undefined).length;
                  
                  return (
                    <TableCell key={game.gameId} align="center">
                      {info ? (
                        <>
                          <span style={{ fontWeight: 'bold' }}>{info.homeAway} {info.otherTeam}</span><br />
                          <span>{info.dateStr} - {info.timeStr}</span><br />
                          {info.venueInfo && <span style={{ fontSize: '0.9rem', fontStyle: 'italic' }}>{info.venueInfo}</span>}<br />
                          <Box sx={{ mt: 1, fontSize: '0.8rem' }}>
                            <Box sx={{ color: 'green' }}>Going: {going}</Box>
                            <Box sx={{ color: 'red' }}>Not Going: {notGoing}</Box>
                            <Box sx={{ color: 'orange' }}>No Response: {noResponse}</Box>
                          </Box>
                        </>
                      ) : (
                        <span>Game</span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {allPlayers.map(player => (
                <TableRow key={player.userTenantId}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Link
                        to={`/player/${player.userTenantId}`}
                        style={{ textDecoration: 'none', color: player.isInjured ? '#9e9e9e' : '#2E4156', fontWeight: 500 }}
                      >
                        {player.name}
                      </Link>
                      {player.isInjured && (
                        <PersonalInjuryIcon
                          sx={{ color: '#d32f2f', fontSize: '1rem' }}
                          titleAccess="Injured"
                        />
                      )}
                    </Box>
                  </TableCell>
                  {games.map(game => {
                    const p = game.players.find(x => x.userId === player.userId);

                    // Injured players show no availability info
                    if (player.isInjured) {
                      return (
                        <TableCell key={game.gameId} align="center">
                          <Typography variant="caption" sx={{ color: '#9e9e9e' }}>—</Typography>
                        </TableCell>
                      );
                    }

                    let responseIcon = null;
                    if (p) {
                      if (p.status === true) {
                        responseIcon = <CheckCircleIcon sx={{ color: 'green' }} />;
                      } else if (p.status === false) {
                        responseIcon = <CancelIcon sx={{ color: 'red' }} />;
                      } else {
                        responseIcon = <HelpOutlineIcon sx={{ color: '#FFA500' }} titleAccess="No response" />;
                      }
                    } else {
                      responseIcon = <span>—</span>;
                    }
                    return (
                      <TableCell key={game.gameId} align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          {responseIcon}
                          {canUpdatePlayerAvailability(player.userTenantId) && (
                            <span style={{ fontSize: '0.7rem', color: '#666' }}>
                              (
                              <button
                                type="button"
                                disabled={refreshing}
                                style={{
                                  fontSize: '0.7rem',
                                  color: refreshing ? '#999' : '#000000',
                                  textDecoration: 'underline',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  cursor: refreshing ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => {
                                  if (!refreshing) {
                                    updateAvailability(game.gameId, player.userTenantId, true, p?.rsvpId);
                                  }
                                }}
                              >
                                yes
                              </button>
                              {' '}
                              <button
                                type="button"
                                disabled={refreshing}
                                style={{
                                  fontSize: '0.7rem',
                                  color: refreshing ? '#999' : '#000000',
                                  textDecoration: 'underline',
                                  background: 'none',
                                  border: 'none',
                                  padding: 0,
                                  cursor: refreshing ? 'not-allowed' : 'pointer'
                                }}
                                onClick={() => {
                                  if (!refreshing) {
                                    updateAvailability(game.gameId, player.userTenantId, false, p?.rsvpId);
                                  }
                                }}
                              >
                                no
                              </button>
                              )
                            </span>
                          )}
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Snackbar
        open={!!updateError}
        autoHideDuration={6000}
        onClose={() => setUpdateError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setUpdateError(null)}>
          {updateError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GameAvailabilityTable;